import { FormProvider, useForm } from 'react-hook-form';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GenericDialog } from 'app/components/Alerts/GenericDialog';
import { Grid, CircularProgress } from '@mui/material';
import { PlacementChangeControls } from './PlacementChangeControls';
import _ from 'lodash';
import { convertToFormat } from 'app/helpers/dateHelper';
import { selectPlacementDetails, selectPlacementHeaderData } from 'store/redux-store/placement-details/selectors';
import { useStyles } from './Helper/Styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Cancel } from '@AMIEWEB/Common/CancelModal/Cancel';

import { newPlacementActions } from 'store/redux-store/new-placement/slice';
import { selectCreatedPlacement } from 'store/redux-store/new-placement/selectors';
import { usePromiseTracker } from 'react-promise-tracker';
import { ChangeOptionsEnum } from './Helper/Types';
import { SessionKey, appendSessionValue } from 'utils/customHooks/sessionStorage/sessionHelpers';
import { ICustomOption } from '../CreatePlacement/CustomComponents/CustomTypeAhead';
import { selectUser } from 'oidc/user.selectors';
import { PlacementChangeModalSourceIndentifier } from 'app/constants';

export const PlacementChangeDialog = props => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const history = useNavigate();
  const { openDialog, closeDialog, source } = props;
  const [cancel, setCancel] = useState<boolean>(false);
  const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
  const [undoDisabled, setUndoDisabled] = useState<boolean>(true);
  const [searchDialogOpen, setSearchDialogOpen] = useState<boolean>(false);
  const [searchResult, setSearchResult] = useState<any>(null);
  const { promiseInProgress } = usePromiseTracker({ area: 'placement-change-submit', delay: 0 });
  const placementDetails = useSelector(selectPlacementDetails);
  const newPlacementId = useSelector(selectCreatedPlacement);
  const { userInfo } = useSelector(selectUser);
  const placementHeaderDetails = useSelector(selectPlacementHeaderData);

  const formMethods = useForm({
    defaultValues: {
      changeOption: null,
      startDate: '',
      facilityID: null,
      orderID: null,
      endDate: null,
      errorMessage: null,
    },
    shouldUnregister: false,
  });
  const {
    getValues,
    formState: { dirtyFields },
  } = formMethods;
  const { changeOption } = getValues();
  const dialogTitles = React.useMemo(() => {
    return {
      Title:
        searchDialogOpen && changeOption === ChangeOptionsEnum.FacilityID
          ? t('placement.placementChange.searchFacilities')
          : searchDialogOpen && changeOption === ChangeOptionsEnum.OrderID
          ? t('placement.placementChange.searchOrders')
          : t('placement.placementChange.title'),
      SaveText: searchDialogOpen
        ? t('placement.placementChange.select')
        : newPlacementId !== null
        ? t('placement.placementChange.gotoPlacement')
        : t('placement.placementChange.create'),
      CancelText: searchDialogOpen
        ? t('placement.placementChange.close')
        : newPlacementId !== null
        ? t('placement.placementChange.close')
        : t('placement.placementChange.cancel'),
    };
  }, [searchDialogOpen, newPlacementId, t]);

  const onSubmitClick = async () => {
    if (searchDialogOpen && searchResult !== null) {
      setSearchDialogOpen(false);
      if (changeOption === ChangeOptionsEnum.FacilityID) {
        appendSessionValue(SessionKey.recentFacilities, {
          primaryLabel: searchResult[0]?.facilityId,
          secondaryLabel: searchResult[0]?.facility,
        } as ICustomOption);
      } else if (changeOption === ChangeOptionsEnum.OrderID) {
        appendSessionValue(SessionKey.recentOrders, {
          primaryLabel: searchResult[0]?.orderId,
          secondaryLabel: searchResult[0]?.facility,
        } as ICustomOption);
      }
    } else if (!searchDialogOpen && !newPlacementId) {
      const { startDate, endDate, facilityID, orderID } = getValues();
      setSaveDisabled(true);

      if (source === PlacementChangeModalSourceIndentifier) {
        dispatch(
          newPlacementActions.changePlacementOrder({
            placementId: placementHeaderDetails?.placementId,
            orderId: orderID?.primaryLabel,
            travelerId: placementHeaderDetails?.candidate.id,
            companyId: placementHeaderDetails?.brandId,
            disciplineId: 0,
            specialtyId: 0,
            employeeId: userInfo?.employeeId,
          }),
        );
      } else {
        dispatch(
          newPlacementActions.getPlacementChangeId({
            copyPlacementOption: changeOption,
            startDate: startDate ? convertToFormat(startDate, 'yyyy-MM-DD') : '',
            endDate: changeOption === ChangeOptionsEnum.PreStart ? convertToFormat(endDate, 'yyyy-MM-DD') : null,
            oldPlacementID: changeOption ? placementDetails?.header?.placementId : 0,
            copyPlacementFacilityID:
              changeOption === ChangeOptionsEnum.FacilityID ? facilityID?.primaryLabel.toString() || null : null,
            copyPlacementOrderID:
              changeOption === ChangeOptionsEnum.OrderID ? orderID?.primaryLabel.toString() || null : null,
            currentEmployeeId: userInfo?.employeeId,
          }),
        );
      }
    } else if (newPlacementId !== null) {
      navigateToPlacement();
    }
  };

  const navigateToPlacement = () => {
    history(`/placement/${newPlacementId}`, { state: { navigateBack: true } });
    dispatch(newPlacementActions.setPlacementCreated(null));
  };

  const handleCancel = () => {
    if (!_.isEmpty(dirtyFields) && newPlacementId === null) setCancel(true);
    else yesAction();
  };
  const yesAction = () => {
    closeDialog();
    setCancel(false);
    dispatch(newPlacementActions.setErrorMessage(null));
    if (newPlacementId !== null) dispatch(newPlacementActions.setPlacementCreated(null));
  };
  const noAction = () => {
    setCancel(false);
  };

  return (
    <>
      <GenericDialog
        variant={'blue'}
        classes={{
          paper: searchDialogOpen ? classes.searchDialogContainer : classes.dialogContainer,
        }}
        open={openDialog}
        disablePortal
        draggable
        disableEscapeKeyDown
        dialogTitleProps={{
          text: dialogTitles?.Title,
          closeButton: true,
          expandable: searchDialogOpen ? true : false,
          onExpandChange: expanded => {
            setUndoDisabled(!expanded);
          },
          undoButton: searchDialogOpen && undoDisabled ? true : false,
          onUndoClick: () => {
            setSearchDialogOpen(false);
            setSearchResult(null);
          },
        }}
        dialogActions={[
          {
            text: dialogTitles?.CancelText,
            variant: 'contained',
            color: 'tertiary',
            onClick: handleCancel,
          },
          {
            text: dialogTitles?.SaveText,
            variant: 'contained',
            disabled: saveDisabled,
            onClick: onSubmitClick,
          },
        ]}
        onClose={handleCancel}
      >
        <FormProvider {...formMethods}>
          <Grid spacing={3} container>
            <Grid item xs={12}>
              {
                <PlacementChangeControls
                  searchDialogOpen={searchDialogOpen}
                  setSearchDialogOpen={setSearchDialogOpen}
                  searchResult={searchResult}
                  setSearchResult={setSearchResult}
                  setSaveDisabled={setSaveDisabled}
                  newPlacementId={newPlacementId}
                  source={source}
                />
              }
              {promiseInProgress && <CircularProgress className={classes.validationLoader} />}
            </Grid>
          </Grid>
        </FormProvider>
      </GenericDialog>
      {cancel && <Cancel openDialog={cancel} handleConfirmAction={yesAction} handleCancelAction={noAction} />}
    </>
  );
};
